/**
 * D-EZ-102 — VICTORY LANE.
 *
 * The moment between the checkered flag and the result sheet: the winner, his
 * car, his shop, the name on the hood, how he won it, what it meant to his
 * career, what was said at the microphone, and the headline the paper will run.
 *
 * Every field is read from something the game already settled:
 *   • the race — winner, start, how it was won (`raceStory.ts`);
 *   • the money books — the primary sponsor on the car (`sponsorSlotsFor`);
 *   • the league's history — milestones reached in THIS race (`careerMilestones`);
 *   • the season ledger — the post-race interview the dice already rolled.
 * The interview's effects are applied by the season engine as they always were;
 * this only shows it at the moment it happened instead of a week later.
 */

import type { RaceResult } from '../race.ts';
import type { FinanceState } from '../finance/raceFinance.ts';
import { sponsorSlotsFor } from '../finance/sponsorNames.ts';
import type { Milestone } from '../history/index.ts';
import { INTERVIEW_BY_CODE } from '../postRaceInterview.ts';
import type { RuleEvent } from '../ruleEvent.ts';
import { hometownFor } from './hometown.ts';
import { carsOf, bigIncidents, howItWasWon, startOf, victoryHeadline, type HowWon } from './raceStory.ts';

export interface InterviewMoment {
  driverId: string;
  name: string;
  /** "Winner", "Wrecked", "Feud"… — the chart's own category, in words. */
  category: string;
  /** The chart's printed quote, verbatim. */
  quote: string | null;
  /** What it does to him next race, verbatim from the chart. */
  action: string | null;
  isWinner: boolean;
}

const CATEGORY_WORDS: Record<string, string> = {
  winner: 'The winner',
  wrecked: 'Wrecked out',
  dnf: 'Did not finish',
  fume: 'Fuming',
  feud: 'Feuding',
  'top-or-middle': 'From the field',
};

/**
 * The post-race interview filed for this race, or null when the dice landed in
 * an empty category (about half of all races) or none was rolled.
 *
 * Reads the SEASON slice of the ledger (`SeasonRaceResult.events`), where the
 * interview is filed after the flag. `interview.applied` is last week's effect
 * being cashed in — it is not an interview and is skipped.
 */
export function interviewFrom(
  events: readonly RuleEvent[] | undefined,
  nameOf: (id: string) => string,
  winnerId: string | null,
): InterviewMoment | null {
  for (const e of events ?? []) {
    if (e.eventType !== 'post-race-interview') continue;
    if (e.momentTag === 'interview.ignored' || e.momentTag === 'interview.applied') continue;
    const band = e.momentTag.replace(/^interview\./, '');
    if (!(band in CATEGORY_WORDS)) continue;
    const driverId = e.actorIds[0];
    if (!driverId) continue;
    const code = e.dice.find((d) => d.code)?.code ?? null;
    const row = code ? INTERVIEW_BY_CODE[String(code)] : undefined;
    const action = e.modifiers.find((m) => m.name === 'action taken')?.value;
    return {
      driverId,
      name: nameOf(driverId),
      category: CATEGORY_WORDS[band],
      quote: row?.quote ?? null,
      action: typeof action === 'string' ? action : null,
      isWinner: driverId === winnerId,
    };
  }
  return null;
}

export interface VictoryLaneModel {
  winnerId: string;
  name: string;
  carNumber: string;
  teamId: string | null;
  teamName: string | null;
  sponsor: string | null;
  hometown: string | null;
  started: number | null;
  how: HowWon | null;
  /** Career milestones reached in this race by the winner, in words. */
  milestones: string[];
  headline: string;
  /** One line under the headline. */
  standfirst: string;
  interview: InterviewMoment | null;
  raceName: string;
  trackName: string;
}

export interface VictoryLaneInput {
  result: RaceResult;
  teams?: readonly { teamId: string; name: string; driverIds: readonly string[] }[];
  /** The books after this race, for the sponsor on the hood. */
  contracts?: FinanceState | null;
  /** Milestones the league recorded IN THIS RACE (any driver). */
  milestones?: readonly Milestone[];
  /** This race's slice of the season ledger, for the interview. */
  seasonEvents?: readonly RuleEvent[];
  /** The defending winner of this named race, going in. */
  defendingWinnerId?: string | null;
}

export function victoryLane(input: VictoryLaneInput): VictoryLaneModel | null {
  const { result } = input;
  const winnerId = result.winnerId;
  if (!winnerId) return null;
  const cars = carsOf(result);
  const card = cars.get(winnerId)?.card;
  const finish = result.finishingOrder.find((f) => f.driverId === winnerId);
  const name = card?.name ?? finish?.name ?? winnerId;
  const team = input.teams?.find((t) => t.driverIds.includes(winnerId)) ?? null;
  const dfs = input.contracts?.byDriver[winnerId];
  const sponsor = dfs ? (sponsorSlotsFor(dfs).find((s) => s.slot === 'Primary')?.name ?? null) : null;
  const how = howItWasWon(result);
  const started = startOf(result, winnerId);
  const mine = (input.milestones ?? []).filter((m) => m.driverId === winnerId);
  const firstWin = mine.some((m) => m.kind === 'first-win');
  const nthWin = mine.find((m) => m.kind === 'nth-win')?.n ?? null;
  const milestoneLines = mine
    .filter((m) => m.kind === 'first-win' || m.kind === 'nth-win' || m.kind === 'drought-ended')
    .map((m) =>
      m.kind === 'first-win'
        ? `First career win${m.counting === 'league' ? ' in this league' : ''}.`
        : m.kind === 'nth-win'
          ? `Career win number ${m.n}.`
          : `Ends a ${m.n}-start winless run.`,
    );
  const defended = Boolean(input.defendingWinnerId && input.defendingWinnerId === winnerId);
  if (defended) milestoneLines.push(`Back-to-back in the ${result.state.raceName}.`);
  const nameOf = (id: string) => cars.get(id)?.card.name ?? result.finishingOrder.find((f) => f.driverId === id)?.name ?? id;
  const bigOne = bigIncidents(result).length > 0;
  const headline = victoryHeadline({
    winnerName: name,
    raceName: result.state.raceName,
    trackName: result.track.name,
    how,
    started,
    firstCareerWin: firstWin,
    winMilestone: nthWin,
    bigOne,
    defendedRace: defended,
  });
  const second = result.finishingOrder.find((f) => f.place === 2);
  const standfirst = [
    started !== null ? `From ${started === 1 ? 'the pole' : `${started}${suffix(started)} on the grid`}` : null,
    second ? `ahead of ${second.name}` : null,
    result.cautions.length > 0 ? `through ${result.cautions.length} caution${result.cautions.length === 1 ? '' : 's'}` : 'without a caution',
  ]
    .filter(Boolean)
    .join(', ');
  return {
    winnerId,
    name,
    carNumber: card?.carNumber ?? '',
    teamId: team?.teamId ?? card?.teamId ?? null,
    teamName: team?.name ?? null,
    sponsor,
    hometown: hometownFor(winnerId),
    started,
    how,
    milestones: milestoneLines,
    headline,
    standfirst: standfirst ? `${standfirst[0].toUpperCase()}${standfirst.slice(1)}.` : '',
    interview: interviewFrom(input.seasonEvents, nameOf, winnerId),
    raceName: result.state.raceName,
    trackName: result.track.name,
  };
}

function suffix(n: number): string {
  const v = n % 100;
  return v >= 11 && v <= 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
}
